import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal, { Actions, PatientName } from 'components/shared/StandardModal';
import { useTranslation } from 'context/translations';
import { simpleDashDate } from 'lib/date';
import LocationEpisode from 'models/LocationEpisode';
import Review, { ReviewStatus, ReviewType } from 'models/Review';
import { activityQueryKeys } from 'services/api/activity';
import { dischargeCalendarQueryKeys } from 'services/api/dischargeCalendar';
import { PlanningDischargeOption, usePlanningDischargeOptions } from 'services/api/dischargeReasons';
import { locationEpisodeQueryKeys } from 'services/api/locationEpisodes';
import {
  createReview,
  CreateReviewInput,
  referralQueryKeys,
  updateReview,
  UpdateReviewInput,
} from 'services/api/reviews';

const FormContainer = styled.div`
  padding-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export type DischargeModalType = {
  setShow: (b: boolean) => void;
  patientName: string;
  review?: Review;

  locationEpisode: LocationEpisode;
};
export default function ProjectedDischargeModal(props: DischargeModalType) {
  const { setShow, patientName, locationEpisode, review } = props;
  const queryClient = useQueryClient();
  const { translate } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: (x: UpdateReviewInput | CreateReviewInput) =>
      review ? updateReview(x as UpdateReviewInput) : createReview(x as CreateReviewInput),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationEpisodeQueryKeys.show({ id: locationEpisode.id }) });
      queryClient.invalidateQueries({ queryKey: activityQueryKeys.base });
      queryClient.invalidateQueries({ queryKey: dischargeCalendarQueryKeys.index() });
      queryClient.invalidateQueries({ queryKey: referralQueryKeys.base });
      cancel();
    },
  });

  const projectedDischargeReview = locationEpisode.projectedDischargeReview;
  const date = projectedDischargeReview?.date;
  const existingPlanningDischargeTo = projectedDischargeReview?.planningDischargeTo ?? null;

  const { options: planningDischargeQuery } = usePlanningDischargeOptions({
    groupType: locationEpisode.group.groupTypeId,
  });

  // Flatten for lookups - Select component handles the infinite query directly
  const planningDischargeOptions = useMemo(
    () => planningDischargeQuery.data?.pages.flatMap((p) => p.data) ?? [],
    [planningDischargeQuery.data]
  );

  const [startDate, setStartDate] = useState<Date | null>(date ?? null);
  const [dateError, setDateError] = useState('');
  const [planningDischargeTo, setPlanningDischargeTo] = useState<PlanningDischargeOption | null>(null);

  const getInitialPlanningDischargeOption = () => {
    if (!existingPlanningDischargeTo) return null;
    return planningDischargeOptions.find((opt) => opt.id === existingPlanningDischargeTo) ?? null;
  };

  useEffect(() => {
    setPlanningDischargeTo(getInitialPlanningDischargeOption());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingPlanningDischargeTo, planningDischargeOptions]);

  const convdate = simpleDashDate(startDate);

  const cancel = () => {
    setShow(false);
    setStartDate(date ?? null);
    setDateError('');
    setPlanningDischargeTo(getInitialPlanningDischargeOption());
  };
  const submit = () => {
    if (!convdate) return setDateError('This field is required');

    const commonFields = {
      projectedDischargeDate: convdate,
      planningDischargeTo: planningDischargeTo?.id ?? null,
      status: ReviewStatus.ACCEPTED,
      include: 'activities',
    };

    mutate(
      review
        ? {
            id: review.id,
            ...commonFields,
          }
        : {
            type: ReviewType.PROJECTED_DISCHARGE,
            locationEpisodeId: locationEpisode.id,
            ...commonFields,
          }
    );
  };

  return (
    <StandardModal title={`Update Projected ${translate('Discharge')}`} onCancel={cancel}>
      <PatientName>Patient: {patientName}</PatientName>
      <FormContainer>
        <InputGroup title={`Projected ${translate('Discharge')} Date`} error={dateError} $containerWidth='auto'>
          <DatePicker
            $hasError={!!dateError}
            minDate={new Date()}
            selected={startDate}
            onChange={(z) => {
              setDateError(z ? '' : 'This field is required');
              setStartDate(z);
            }}
          />
        </InputGroup>
        <InputGroup title={`Planning ${translate('Discharge')} To (optional)`} $containerWidth='auto'>
          <Select<PlanningDischargeOption>
            options={planningDischargeQuery}
            placeholder='Select Destination'
            getOptionLabel={(opt) => opt.name}
            getOptionValue={(opt) => opt.id}
            onChange={(change) => setPlanningDischargeTo(change ?? null)}
            value={planningDischargeTo}
            isClearable
          />
        </InputGroup>
      </FormContainer>
      <Actions>
        <Button variant='ghost' onClick={cancel}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!convdate} loading={isPending}>
          Confirm
        </Button>
      </Actions>
    </StandardModal>
  );
}
